import { HStack, Stack } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import {
  ArrowUpOnSquareStackIcon,
  ListBulletIcon,
  QueueListIcon,
} from '@heroicons/react/24/outline';
import { Heading } from '@main/complykit';
import { Status } from '@main/complykit/components/status';
import { Tag } from '@main/complykit/components/tag';
import { useMarkVendorQuestionnaireAsSubmittedMutation } from '@main/graphql/mutations/MarkVendorQuestionnaireAsSubmitted.vendor.generated';
import { Vendor_Questionnaire_Statuses_Enum } from '@main/graphql/types.vendor.generated';
import {
  QuestionnaireFormContextValue,
  QuestionnnaireFormStats,
  useQuestionnaireFormStats,
} from '@main/questionnaires-form';
import { flattenFields } from '@main/questionnaires-form/utils';
import { getQuestionnaireType, QuestionnaireType, useStableCallback } from '@main/shared/browser';
import { toError, VendorQuestionnaireTabKeys } from '@main/shared/utils';
import {
  actionHelper,
  Drawer,
  DrawerActionsProps,
  errorToast,
  getVisibleTabs,
  successToast,
  useAlertDialog,
} from '@main/ui';
import { QUESTIONNAIRE_STATUSES } from '@main/vendors';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { VendorQuestionnaireDetailsTab } from './details-tab';
import {
  GetVendorQuestionnaireDrawerQuery,
  useGetVendorQuestionnaireDrawerQuery,
} from './drawer.vendor.generated';
import { VendorQuestionnaireQuestionsTab } from './questions-tab';
import { VendorQuestionnaireSubmissionTab } from './submission-tab';

export interface VendorQuestionnaireDrawerProps {
  questionnaireId: string;
}

export function VendorQuestionnaireDrawer({ questionnaireId }: VendorQuestionnaireDrawerProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useGetVendorQuestionnaireDrawerQuery({ questionnaireId });

  const questionnaire = data?.vendor_questionnaires_by_pk;
  const isSubmitted = questionnaire?.status === Vendor_Questionnaire_Statuses_Enum.Submitted;
  const type = getQuestionnaireType(questionnaire?.questionnaire);
  const fields = useMemo(
    () => questionnaire?.form?.config_snapshot ?? [],
    [questionnaire?.form?.config_snapshot],
  );
  const flatFields = useMemo(() => flattenFields(fields), [fields]);
  const context: QuestionnaireFormContextValue = useMemo(
    () => ({
      flatFields,
      fieldsMap: new Map(flatFields.map((f) => [f.name, f])),
      questionFields: [],
      answers: questionnaire?.form?.answers ?? [],
      answersMap: new Map(
        questionnaire?.form?.answers.map((answer) => [answer.field_name, answer]),
      ),
      filtersFilter: () => false,
    }),
    [flatFields, questionnaire?.form?.answers],
  );
  const stats = useQuestionnaireFormStats(fields, context);

  const validator = useRef<() => Promise<boolean>>();
  const setValidator = useStableCallback(
    (validatorFn: () => Promise<boolean>) => (validator.current = validatorFn),
  );
  const drawerActions = useDrawerActions({
    questionnaireId,
    questionnaireFormStats: stats,
    onValidate: validator.current,
  });
  const { tabs } = useVendorQuestionnaireDrawerTabs(questionnaire, setValidator);
  const [wasSubmitted, setWasSubmitted] = useState(isSubmitted);

  useEffect(() => {
    if (!wasSubmitted && isSubmitted) {
      setWasSubmitted(true);
    } else if (wasSubmitted && !isSubmitted) {
      setWasSubmitted(false);
      errorToast(t('vendors.questionnaires.toasts.submitFailed'));
    }
  }, [wasSubmitted, isSubmitted, t]);

  const toolbarInfo =
    type === QuestionnaireType.Form
      ? t('vendors.questionnaires.drawer.completedQuestions', stats)
      : undefined;

  return (
    <Drawer.Layout isLoading={isLoading} canView isNotFound={!questionnaire}>
      <Drawer.Toolbar {...drawerActions} info={toolbarInfo} />
      <Drawer.Tabs tabs={tabs} isLazy>
        <VendorQuestionnaireDrawerHeader questionnaireId={questionnaireId} />
      </Drawer.Tabs>
    </Drawer.Layout>
  );
}

function useDrawerActions({
  questionnaireId,
  questionnaireFormStats,
  onValidate,
}: {
  questionnaireId: string;
  questionnaireFormStats: QuestionnnaireFormStats;
  onValidate?(): Promise<boolean>;
}): DrawerActionsProps {
  const { t } = useTranslation();
  const { openDialog } = useAlertDialog();
  const { data, refetch } = useGetVendorQuestionnaireDrawerQuery({ questionnaireId });
  const [submitVendorQuestionnaire, { isLoading: isSubmitting }] =
    useMarkVendorQuestionnaireAsSubmittedMutation();
  const questionnaire = data?.vendor_questionnaires_by_pk;
  const type = getQuestionnaireType(questionnaire?.questionnaire);

  if (
    type !== QuestionnaireType.Form ||
    questionnaire?.status === Vendor_Questionnaire_Statuses_Enum.Submitted
  ) {
    return {};
  }

  const isQuestionnareFormCompleted =
    questionnaireFormStats.total === questionnaireFormStats.completed;

  async function submitFormQuestionnaire() {
    if (!isQuestionnareFormCompleted) {
      return;
    }

    if (onValidate && !(await onValidate())) {
      return;
    }

    try {
      await submitVendorQuestionnaire({ vendorQuestionnaireId: questionnaireId }).unwrap();
      successToast(t('vendors.questionnaires.toasts.submitSuccess'));
      await refetch().unwrap();
    } catch (error) {
      errorToast(t('vendors.questionnaires.toasts.submitFailed'));
      datadogLogs.logger.error(
        'Error while submitting vendor questionnaire',
        { questionnaireId },
        toError(error),
      );
    }
  }

  return {
    primaryAction: actionHelper.primaryAction({
      label: t('vendors.questionnaires.drawer.submit'),
      isDisabled: !isQuestionnareFormCompleted,
      disabledTooltip: t('vendors.questionnaires.drawer.questionnaireNotCompleted'),
      isLoading: isSubmitting,
      onClick: () =>
        openDialog({
          dialogHeader: t('vendors.questionnaires.submitAlert.header'),
          dialogContent: t('vendors.questionnaires.submitAlert.content'),
          confirmAction: {
            colorScheme: 'blue',
            children: t('buttons.submit'),
            onClick: submitFormQuestionnaire,
          },
        }),
    }),
  };
}

const VendorQuestionnaireDrawerHeader = ({ questionnaireId }: { questionnaireId: string }) => {
  const { t } = useTranslation();
  const { data } = useGetVendorQuestionnaireDrawerQuery({ questionnaireId });
  const questionnaire = data?.vendor_questionnaires_by_pk;
  const statusMeta =
    QUESTIONNAIRE_STATUSES[questionnaire?.status ?? Vendor_Questionnaire_Statuses_Enum.Pending];

  return (
    <Stack spacing={4}>
      <HStack spacing={2}>
        <Tag color="purple">{t('entities.questionnaire')}</Tag>
        <Status tone={statusMeta.tone} data-testid="vendor-questionnaire-status">
          {t(statusMeta.value)}
        </Status>
      </HStack>

      <Heading size="md">{questionnaire?.questionnaire.name}</Heading>
    </Stack>
  );
};

const useVendorQuestionnaireDrawerTabs = (
  questionnaire: GetVendorQuestionnaireDrawerQuery['vendor_questionnaires_by_pk'],
  getValidator?: (validator: () => Promise<boolean>) => void,
) => {
  const { t } = useTranslation();

  const tabs = useMemo(() => {
    if (!questionnaire) {
      return [];
    }

    const type = getQuestionnaireType(questionnaire.questionnaire);

    return getVisibleTabs<VendorQuestionnaireTabKeys>({
      details: {
        label: t('vendors.questionnaires.tabs.details.title'),
        icon: ListBulletIcon,
        panel: <VendorQuestionnaireDetailsTab questionnaireId={questionnaire.id} />,
        hideTabTitle: true,
      },
      files: {
        label: t('vendors.questionnaires.tabs.submission.title'),
        icon: ArrowUpOnSquareStackIcon,
        panel: <VendorQuestionnaireSubmissionTab questionnaireId={questionnaire.id} />,
        isHidden: type !== QuestionnaireType.File,
      },
      form: {
        label: t('vendors.questionnaires.tabs.questions.title'),
        hideTabTitle: true,
        icon: QueueListIcon,
        panel: (
          <VendorQuestionnaireQuestionsTab
            questionnaireId={questionnaire.id}
            getValidator={getValidator}
          />
        ),
        isHidden: type !== QuestionnaireType.Form,
      },
    });
  }, [questionnaire, t, getValidator]);

  return { tabs };
};
